/**
 * Which popup, if any, currently owns the arrow keys. A claim suppresses both
 * document-level arrow behaviors: `useCalendarKeyboardScroll` (up/down row
 * scrolling) and `useCalendarArrowNavigation` (left/right date navigation).
 *
 * `useCalendarKeyboardScroll` listens on `document` and scrolls the calendar a
 * row per arrow press. That scroll dispatches `calendarContainerScrollPosition`,
 * which the calendar's popups read as "the coach scrolled" and close on — so an
 * open popup navigating with the arrows would close itself on its own keypress.
 * Left/right matters just as much: the options menu opens submenus with
 * ArrowRight, and the pace and superset pickers close on ArrowLeft.
 *
 * A module-level count rather than focus detection or `stopImmediatePropagation`:
 * the options menu is highlight-only, so DOM focus stays on the table input (or
 * `<body>`) and there is nothing in the event to detect, and listener
 * registration order isn't ours to rely on.
 */

let claimCount = 0;

/**
 * Claim the arrow keys for an open popup. Returns its own release function, so
 * call sites can return it straight out of a `useEffect`. Counted rather than
 * boolean because a nested picker can claim while its parent menu still holds.
 *
 * @returns {() => void} release — idempotent
 */
export const claimArrowKeys = () => {
  claimCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    claimCount = Math.max(0, claimCount - 1);
  };
};

export const hasArrowKeyOwner = () => claimCount > 0;

/** Test-only: keep a leaked claim from bleeding into the next case. */
export const resetArrowKeyOwnership = () => {
  claimCount = 0;
};
